/**
 * Vehicle API - All vehicle-related operations
 * @module api/vehicles
 */

import { API_BASE_URL, apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// VEHICLE CRUD API
// =============================================================================

/**
 * Vehicle API - CRUD operations for fleet vehicles
 */
export const vehicleAPI = {
  getAll: () => apiRequest('/vehicle?_limit=5000'),
  getById: (id) => apiRequest(`/vehicle/${id}`),
  create: (vehicle) => apiRequest('/vehicle', {
    method: 'POST',
    body: JSON.stringify(vehicle),
  }),
  update: (id, vehicle) => apiRequest(`/vehicle/${id}`, {
    method: 'PUT',
    body: JSON.stringify(vehicle),
  }),
  delete: (id) => apiRequest(`/vehicle/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// VEHICLE DOCUMENT API
// =============================================================================

/**
 * Vehicle Document API - Documents attached to vehicles
 */
export const vehicleDocumentAPI = {
  getAll: () => apiRequest('/vehicle_document'),
  getById: (id) => apiRequest(`/vehicle_document/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/vehicle_document?vehicle_id=${vehicleId}`),
  getExpiringSoon: (days = 30) => apiRequest(`/vehicle_document?expiring_within_days=${days}`),
  create: (document) => apiRequest('/vehicle_document', {
    method: 'POST',
    body: JSON.stringify(document),
  }),
  update: (id, document) => apiRequest(`/vehicle_document/${id}`, {
    method: 'PUT',
    body: JSON.stringify(document),
  }),
  delete: (id) => apiRequest(`/vehicle_document/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// VEHICLE INSURANCE API
// =============================================================================

/**
 * Vehicle Insurance API - Insurance coverage per vehicle
 */
export const vehicleInsuranceAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle_insurance${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiRequest(`/vehicle_insurance/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/vehicle_insurance?vehicle_id=${vehicleId}`),
  create: (vehicleInsurance) => apiRequest('/vehicle_insurance', {
    method: 'POST',
    body: JSON.stringify(vehicleInsurance),
  }),
  update: (id, vehicleInsurance) => apiRequest(`/vehicle_insurance/${id}`, {
    method: 'PUT',
    body: JSON.stringify(vehicleInsurance),
  }),
  delete: (id) => apiRequest(`/vehicle_insurance/${id}`, {
    method: 'DELETE',
  }),
};

/**
 * Coverage Model API - Insurance coverage templates (Tous Risques, Tiers +, etc.)
 */
export const coverageModelAPI = {
  getAll: () => apiRequest('/coverage_model'),
};

// =============================================================================
// VEHICLE EXIT API (Outboarding Workflow)
// =============================================================================

/**
 * Vehicle Exit API - Outboarding workflow for vehicle disposal
 */
export const vehicleExitAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle-exits${queryString ? '?' + queryString : ''}`);
  },

  getById: (id) => apiRequest(`/vehicle-exits/${id}`),

  getByVehicle: (vehicleId) => apiRequest(`/vehicle-exits?vehicle_id=${vehicleId}`),

  create: (exitData) => apiRequest('/vehicle-exits', {
    method: 'POST',
    body: JSON.stringify(exitData),
  }),

  update: (id, updates) => apiRequest(`/vehicle-exits/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),

  delete: (id) => apiRequest(`/vehicle-exits/${id}`, {
    method: 'DELETE',
  }),

  complete: (id) => apiRequest(`/vehicle-exits/${id}/complete`, {
    method: 'POST',
  }),

  cancel: (id, reason = '') => apiRequest(`/vehicle-exits/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  addInsuranceLink: (exitId, linkData) => apiRequest(`/vehicle-exits/${exitId}/insurance-links`, {
    method: 'POST',
    body: JSON.stringify(linkData),
  }),

  updateInsuranceLink: (exitId, linkId, updates) => apiRequest(`/vehicle-exits/${exitId}/insurance-links/${linkId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),

  downloadInsuranceEmail: async (exitId, linkId = null) => {
    const url = linkId
      ? `${API_BASE_URL}/vehicle-exits/${exitId}/insurance-email?link_id=${linkId}`
      : `${API_BASE_URL}/vehicle-exits/${exitId}/insurance-email`;

    const res = await fetch(url, {
      headers: getAuthHeaders()
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to generate email');
    }

    const disposition = res.headers.get('Content-Disposition');
    let filename = 'resiliation.eml';
    if (disposition) {
      const matches = disposition.match(/filename="(.+)"/);
      if (matches) filename = matches[1];
    }

    const blob = await res.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
  },

  markInsuranceEmailSent: (exitId, linkId = null) => apiRequest(`/vehicle-exits/${exitId}/insurance-email/mark-sent`, {
    method: 'POST',
    body: JSON.stringify({ link_id: linkId }),
  }),

  getOverdue: () => apiRequest('/vehicle-exits/reports/overdue'),

  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle-exits/reports/stats${queryString ? '?' + queryString : ''}`);
  },
};

// =============================================================================
// VEHICLE ASSIGNMENT API
// =============================================================================

/**
 * Vehicle Assignment API - Driver-vehicle assignments
 */
export const vehicleAssignmentAPI = {
  getAll: () => apiRequest('/vehicle_assignment'),
  getById: (id) => apiRequest(`/vehicle_assignment/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/vehicle_assignment?vehicle_id=${vehicleId}`),
  getByDriver: (driverId) => apiRequest(`/vehicle_assignment?driver_id=${driverId}`),
  getActive: () => apiRequest('/vehicle_assignment?status=active'),
  create: (assignment) => apiRequest('/vehicle_assignment', {
    method: 'POST',
    body: JSON.stringify(assignment),
  }),
  update: (id, assignment) => apiRequest(`/vehicle_assignment/${id}`, {
    method: 'PUT',
    body: JSON.stringify(assignment),
  }),
  delete: (id) => apiRequest(`/vehicle_assignment/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// VEHICLE SITE ASSIGNMENT API (vehicle_locations table)
// =============================================================================

/**
 * ⚠️ VOCABULARY CLARIFICATION:
 * This API accesses the `vehicle_locations` table, which is MISLEADING.
 *
 * - vehicle_locations = Site assignment history (which depot/garage over time)
 * - vehicle_telemetry = GPS tracking data (lat/lon positions)
 * - vehicles.working_site_id = Home site (primary parking location)
 *
 * See: TELEMATICS_VOCABULARY_GLOSSARY.md
 */
export const vehicleLocationAPI = {
  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  getAll: () => apiRequest('/vehicle_location'),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  getById: (id) => apiRequest(`/vehicle_location/${id}`),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  getByVehicle: (vehicleId) => apiRequest(`/vehicle_location?vehicle_id=${vehicleId}`),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  getCurrent: () => apiRequest('/vehicle_location?is_current=1'),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  create: (location) => apiRequest('/vehicle_location', {
    method: 'POST',
    body: JSON.stringify(location),
  }),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  update: (id, location) => apiRequest(`/vehicle_location/${id}`, {
    method: 'PUT',
    body: JSON.stringify(location),
  }),

  /**
   * @deprecated Use vehicleSiteAssignmentAPI instead for clarity
   */
  delete: (id) => apiRequest(`/vehicle_location/${id}`, {
    method: 'DELETE',
  }),
};

/**
 * Vehicle Site Assignment API - Clear semantic name for vehicle_locations table
 *
 * This API tracks which site (depot/garage) a vehicle was assigned to over time.
 * This is NOT GPS data! For GPS positions, use vehicleTelemetryAPI.
 */
export const vehicleSiteAssignmentAPI = {
  /**
   * Get all site assignment records
   */
  getAll: () => apiRequest('/vehicle_location'),

  /**
   * Get site assignment by ID
   */
  getById: (id) => apiRequest(`/vehicle_location/${id}`),

  /**
   * Get site assignment history for a vehicle
   * @param {number} vehicleId - Vehicle ID
   * @returns {Promise} Array of site assignments (chronological)
   */
  getHistoryByVehicle: (vehicleId) => apiRequest(`/vehicle_location?vehicle_id=${vehicleId}`),

  /**
   * Get current site assignment for all vehicles
   */
  getCurrentAssignments: () => apiRequest('/vehicle_location?is_current=1'),

  /**
   * Create new site assignment
   * @param {Object} assignment - { vehicle_id, site_id, assigned_date, notes }
   */
  create: (assignment) => apiRequest('/vehicle_location', {
    method: 'POST',
    body: JSON.stringify(assignment),
  }),

  /**
   * Update site assignment
   */
  update: (id, assignment) => apiRequest(`/vehicle_location/${id}`, {
    method: 'PUT',
    body: JSON.stringify(assignment),
  }),

  /**
   * Delete site assignment
   */
  delete: (id) => apiRequest(`/vehicle_location/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// VEHICLE TELEMATICS API (vehicle_telemetry table - GPS Data)
// =============================================================================

/**
 * Vehicle Telematics API - GPS tracking data (lat/lon positions)
 *
 * This API accesses the `vehicle_telemetry` table which contains:
 * - Real-time GPS positions (latitude, longitude)
 * - Speed, heading, odometer
 * - Engine status, fuel level, battery voltage
 *
 * ⚠️ VOCABULARY: This is NOT the same as vehicle_locations!
 * - vehicle_telemetry = GPS position data (this API)
 * - vehicle_locations = Site assignment history (vehicleSiteAssignmentAPI)
 * - vehicles.working_site_id = Home site (vehicleAPI.getById)
 */
export const vehicleTelemetryAPI = {
  /**
   * Get latest GPS position for a vehicle
   * @param {number} vehicleId - Vehicle ID
   * @returns {Promise<Object>} Latest position with timestamp, lat/lon, speed, etc.
   */
  getLatestGpsPosition: async (vehicleId) => {
    const response = await apiRequest(`/vehicle_telemetry?vehicle_id=${vehicleId}&_limit=1&_sort=timestamp&_order=desc`);
    return response.ok ? (response.data && response.data[0]) || null : null;
  },

  /**
   * Get latest GPS positions for all vehicles
   * @returns {Promise<Array>} Latest position per vehicle
   */
  getLatestGpsPositions: () => apiRequest('/telematics/latest'),

  /**
   * Get GPS position history for a vehicle (date range)
   * @param {number} vehicleId - Vehicle ID
   * @param {string} startDate - ISO date (YYYY-MM-DD)
   * @param {string} endDate - ISO date (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of GPS positions in chronological order
   */
  getGpsHistory: (vehicleId, startDate, endDate) => {
    const params = new URLSearchParams({
      vehicle_id: vehicleId,
      timestamp_gte: startDate,
      timestamp_lte: endDate,
      _sort: 'timestamp',
      _order: 'asc',
    });
    return apiRequest(`/vehicle_telemetry?${params.toString()}`);
  },

  /**
   * Get recent GPS positions for a vehicle (last N positions)
   * @param {number} vehicleId - Vehicle ID
   * @param {number} limit - Number of positions (default: 100)
   * @returns {Promise<Array>} Recent positions in reverse chronological order
   */
  getRecentGpsPositions: (vehicleId, limit = 100) => {
    return apiRequest(`/vehicle_telemetry?vehicle_id=${vehicleId}&_limit=${limit}&_sort=timestamp&_order=desc`);
  },

  /**
   * Get GPS positions within a geographic bounding box
   * @param {Object} bounds - { minLat, maxLat, minLon, maxLon }
   * @param {string} startDate - Optional ISO date
   * @param {string} endDate - Optional ISO date
   * @returns {Promise<Array>} Positions within bounds
   */
  getPositionsInBounds: (bounds, startDate = null, endDate = null) => {
    const params = new URLSearchParams({
      latitude_gte: bounds.minLat,
      latitude_lte: bounds.maxLat,
      longitude_gte: bounds.minLon,
      longitude_lte: bounds.maxLon,
    });
    if (startDate) params.append('timestamp_gte', startDate);
    if (endDate) params.append('timestamp_lte', endDate);
    return apiRequest(`/vehicle_telemetry?${params.toString()}`);
  },

  /**
   * Get all GPS telemetry data (admin only, use with caution)
   * @param {Object} params - Query parameters (_limit, _offset, etc.)
   * @returns {Promise<Array>} Telemetry data
   */
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle_telemetry${query ? '?' + query : ''}`);
  },
};

/**
 * Vehicle Home Site API - Semantic wrapper for vehicles.working_site_id
 *
 * This API provides clear access to a vehicle's primary parking location (home site).
 * This is NOT GPS data! For GPS positions, use vehicleTelemetryAPI.
 */
export const vehicleHomeSiteAPI = {
  /**
   * Get vehicle's home site (primary parking location)
   * @param {number} vehicleId - Vehicle ID
   * @returns {Promise<Object>} Vehicle object with working_site_id
   */
  getHomeSite: async (vehicleId) => {
    const response = await apiRequest(`/vehicle/${vehicleId}`);
    if (response.ok && response.data) {
      return {
        vehicleId: response.data.id,
        working_site_id: response.data.working_site_id,
        site_name: response.data.site_name, // Populated if joined
      };
    }
    return null;
  },

  /**
   * Update vehicle's home site
   * @param {number} vehicleId - Vehicle ID
   * @param {number} siteId - Working site ID (depot/garage)
   * @returns {Promise<Object>} Updated vehicle
   */
  updateHomeSite: (vehicleId, siteId) => {
    return apiRequest(`/vehicle/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify({ working_site_id: siteId }),
    });
  },
};

// =============================================================================
// VEHICLE CATALOG API (Reference Database)
// =============================================================================

/**
 * Vehicle Catalog API - Standalone reference database for car specs
 */
export const vehicleCatalogAPI = {
  // Car brands
  getBrands: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle-catalog/brands${query ? '?' + query : ''}`);
  },
  getBrandById: (id) => apiRequest(`/vehicle-catalog/brands/${id}`),

  // Car models
  getModels: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle-catalog/models${query ? '?' + query : ''}`);
  },
  getModelById: (id) => apiRequest(`/vehicle-catalog/models/${id}`),

  // Car versions (full specs)
  getVersions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/vehicle-catalog/versions${query ? '?' + query : ''}`);
  },
  getVersionById: (id) => apiRequest(`/vehicle-catalog/versions/${id}`),

  // Search
  search: (q, params = {}) => {
    const query = new URLSearchParams({ q, ...params }).toString();
    return apiRequest(`/vehicle-catalog/search?${query}`);
  },
  quickSearch: (q, limit = 10) => apiRequest(`/vehicle-catalog/quick-search?q=${encodeURIComponent(q)}&limit=${limit}`),

  // Reference data
  getStats: () => apiRequest('/vehicle-catalog/stats'),
  getMotorizations: () => apiRequest('/vehicle-catalog/motorizations'),
  getBodyTypes: () => apiRequest('/vehicle-catalog/body-types'),
  getYearRange: () => apiRequest('/vehicle-catalog/years'),

  // Cascading filters (for dependent dropdowns)
  cascade: {
    getModels: (brand) => apiRequest(`/vehicle-catalog/cascade/models?brand=${encodeURIComponent(brand)}`),
    getMotorizations: (brand, model) =>
      apiRequest(`/vehicle-catalog/cascade/motorizations?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`),
    getFiscalPowers: (brand, model, motorization) =>
      apiRequest(`/vehicle-catalog/cascade/fiscal-powers?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}&motorization=${encodeURIComponent(motorization)}`),
    getYears: (brand, model) =>
      apiRequest(`/vehicle-catalog/cascade/years?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`),
    getVersions: (params) => {
      const query = new URLSearchParams(params).toString();
      return apiRequest(`/vehicle-catalog/cascade/versions?${query}`);
    },
  },
};

// =============================================================================
// TRUCK CATALOG API
// =============================================================================

/**
 * Truck Catalog API - Reference database for trucks
 */
export const truckCatalogAPI = {
  getBrands: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/truck-catalog/brands${query ? '?' + query : ''}`);
  },
  getModels: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/truck-catalog/models${query ? '?' + query : ''}`);
  },
  getModelById: (id) => apiRequest(`/truck-catalog/models/${id}`),
  search: (q, params = {}) => {
    const query = new URLSearchParams({ q, ...params }).toString();
    return apiRequest(`/truck-catalog/search?${query}`);
  },
};

// =============================================================================
// VEHICLE RESOURCE API
// =============================================================================

/**
 * Vehicle Resource API - Consumables, wear items, and lifecycle scheduling
 */
export const vehicleResourceAPI = {
  /**
   * Get current resource levels for a vehicle
   * @param {number} vehicleId
   * @returns {Promise<Array>} Current readings with status
   */
  getCurrentByVehicle: (vehicleId) => apiRequest(`/vehicles/${vehicleId}/resources/current`),

  /**
   * Get historical readings for a resource type
   * @param {number} vehicleId
   * @param {string} type - Resource type (fuel_level, oil_level, etc.)
   * @param {string} from - ISO date
   * @param {string} to - ISO date
   * @returns {Promise<Array>} Historical readings
   */
  getHistory: (vehicleId, type, from, to) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    return apiRequest(`/vehicles/${vehicleId}/resources/history?${params.toString()}`);
  },

  /**
   * Get resources in warning/critical status
   * @param {number} vehicleId
   * @returns {Promise<Array>} Alert resources
   */
  getAlerts: (vehicleId) => apiRequest(`/vehicles/${vehicleId}/resources/alerts`),

  /**
   * Get lifecycle schedules for a vehicle
   * @param {number} vehicleId
   * @returns {Promise<Array>} Schedules with due dates
   */
  getSchedule: (vehicleId) => apiRequest(`/vehicles/${vehicleId}/resources/schedule`),

  /**
   * Create or update a schedule
   * @param {number} vehicleId
   * @param {Object} schedule - Schedule data
   * @returns {Promise<Object>} Created/updated schedule
   */
  saveSchedule: (vehicleId, schedule) =>
    apiRequest(`/vehicles/${vehicleId}/resources/schedule`, {
      method: 'POST',
      body: JSON.stringify(schedule),
    }),

  /**
   * Add a new resource reading
   * @param {number} vehicleId
   * @param {Object} reading - Reading data
   * @returns {Promise<Object>} Created reading
   */
  createReading: (vehicleId, reading) =>
    apiRequest(`/vehicles/${vehicleId}/resources/reading`, {
      method: 'POST',
      body: JSON.stringify(reading),
    }),

  /**
   * Add multiple readings at once (inspection)
   * @param {number} vehicleId
   * @param {Array} readings - Array of reading objects
   * @param {number} odometer_km - Current odometer
   * @returns {Promise<Object>} Results summary
   */
  createBulkReadings: (vehicleId, readings, odometer_km) =>
    apiRequest(`/vehicles/${vehicleId}/resources/bulk`, {
      method: 'POST',
      body: JSON.stringify({ readings, odometer_km }),
    }),

  /**
   * Get org-wide alert summary
   * @returns {Promise<Object>} Summary with counts by status
   */
  getAlertsSummary: () => apiRequest(`/resources/alerts/summary`),
};

// =============================================================================
// VEHICLE USAGES API (lot 2 « le temps » §2.3)
// =============================================================================

/**
 * Usages d'un véhicule — qui l'a eu, qui l'a maintenant, qui l'aura.
 *
 * Client UNIQUE de `GET /api/v1/vehicles/:id/usages`. Contrat serveur :
 * `backend/routes/vehicle_usages_routes.mjs` (en-tête du fichier).
 *
 * ⚠️ `site_id` est le rattachement SNAPSHOT de la réservation : `null` se lit
 * « rattachement inconnu », jamais « le site actuel du véhicule ».
 */
export const vehicleUsagesAPI = {
  /**
   * @param {number} vehicleId
   * @param {{from?: string, to?: string}} [range] - dates civiles (AAAA-MM-JJ) ou ISO
   * @returns {Promise<Object>} { ok, vehicle_id, usages: { past, current, upcoming }, coverage }
   */
  getByVehicle: (vehicleId, { from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return apiRequest(`/vehicles/${vehicleId}/usages${qs ? `?${qs}` : ''}`);
  },

  /**
   * Planning d'un site : réservations confirmées à venir des véhicules du site.
   * ⚠️ Attache COURANTE (`vehicle.working_site_id`), pas snapshot — voulu.
   * @param {number} siteId
   * @param {{from?: string, to?: string}} [range]
   */
  getSitePlanning: (siteId, { from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return apiRequest(`/sites/${siteId}/planning${qs ? `?${qs}` : ''}`);
  },
};
